import _ from 'lodash'
import { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { FeatureFlags } from 'constants/security'
import { WrappedComponentType } from 'constants/types'
import { getFormBuilderStructure } from 'services/formBuilderSettings/constants'
import { FormBuilderField, FormBuilderGroup, FormBuilderSection, FormTypes } from 'services/formBuilderSettings/models'

import appSelectors from 'services/app/selectors'
import { withFormBuilderSettingsService, withFormBuilderSettingsServiceProps } from 'services/formBuilderSettings'
import formBuilderSettingsSelectors from 'services/formBuilderSettings/selectors'
import { withNurseryContextService, withNurseryContextServiceProps } from 'services/nurseryContext'
import nurseryContextSelectors from 'services/nurseryContext/selectors'
import { withRouter, withRouterProps } from 'services/router'
import { withSnackbarService, withSnackbarServiceProps } from 'services/utils/snackbar'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import FormBuilderView from './FormBuilderView'

const NURSERY_SETTINGS_GROUPS = {
  read: [
    'nursery.settings',
    'nurserySettings',
    'nurserySettings.formBuilder',
    'formBuilderSettings',
  ],
}

const mapState = (state: RootState, { params }) => ({
  nurseryFormBuilderSettings: nurseryContextSelectors.getNurseryFormBuilderSettings(state),
  nurseryOptions: appSelectors.getContextNurseryRouterConfig(state, params),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

type FormBuilderContainerFullProps = PropsFromRedux
  & withRouterProps
  & withFormBuilderSettingsServiceProps
  & withNurseryContextServiceProps
  & withSnackbarServiceProps

const FormBuilderContainer: WrappedComponentType<FormBuilderContainerFullProps> = ({
  formBuilderSettingsActions,
  location,
  nurseryContextActions,
  nurseryFormBuilderSettings,
  nurseryOptions,
  router,
  snackbarActions,
}) => {
  const [isFetching, setIsFetching] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const formBuilderStructure = getFormBuilderStructure(i18n)

  useEffect(() => {
    if (nurseryOptions.id) {
      nurseryContextActions.getSettings({
        onSuccess: () => setIsInitialLoading(false),
        params: [nurseryOptions.id, {
          groups: NURSERY_SETTINGS_GROUPS,
        }],
      })
    }
  }, [nurseryOptions.id])

  const { group: groupLocation, section: sectionLocation } = location.query || {}
  const firstSection = formBuilderStructure[0]
  const finalSection = _.find(formBuilderStructure, { key: sectionLocation }) || firstSection
  const [navigation, setNavigation] = useState<{ group: FormBuilderGroup | null, section: FormBuilderSection }>({
    group: 1 < finalSection.groups.length
      ? (_.find(firstSection.groups, { key: groupLocation }) || firstSection.groups[0])
      : null,
    section: finalSection,
  })

  useEffect(() => {
    const query = {
      section: navigation.section.key,
    } as { group?: string, section: string }

    if (navigation.group) {
      query.group = navigation.group.key
    } else {
      delete query.group
    }

    router.replace({
      pathname: location.pathname,
      query,
    })
  }, [navigation])

  const handleFieldChange = async (value: { field: FormBuilderField, formType?: FormTypes, value: any }) => {
    setIsFetching(true)

    const payload = formBuilderSettingsSelectors.getPayload(
      nurseryFormBuilderSettings,
      { [value.field.key]: value.value },
    )

    await formBuilderSettingsActions.update({
      body: payload,
      onSuccess: () => {
        snackbarActions.show({
          message: i18n.t('module:Management:FormBuilder:success'),
        })
      },
      params: [nurseryOptions.id, {
        groups: NURSERY_SETTINGS_GROUPS,
      }],
    })

    setIsFetching(false)
  }

  const handleNavigate = (section: FormBuilderSection, group: FormBuilderGroup = null) => {
    let finalGroup = group

    if (!finalGroup && 1 < section.groups.length) {
      finalGroup = section.groups[0]
    }

    setNavigation({
      group: finalGroup,
      section,
    })
  }

  return (
    <FormBuilderView
      isFetching={isFetching}
      isInitialLoading={isInitialLoading}
      navigation={navigation}
      nurseryFormBuilderSettings={nurseryFormBuilderSettings}
      onFieldChange={handleFieldChange}
      onNavigate={handleNavigate}
    />
  )
}

const enhance = compose(
  withRouter,
  withNurseryContextService,
  withFormBuilderSettingsService,
  withSnackbarService,
  connector,
)

FormBuilderContainer.authParams = {
  flags: [
    FeatureFlags.REGISTRATION_FORMS,
  ],
}

export default enhance(FormBuilderContainer)
